import type { IsoDate } from '@sl/core';
import { Banner, Button } from '../../ui';
import { asSentence, messageText } from '../messages';
import { testId } from '../testId';
import { SHELL_COPY } from './copy';
import { useShell } from './shellContext';
import type { ShellLive } from './useShellLive';
import styles from './AppShell.module.css';

// The two live-data states that need a PAGE and not a caption — `mockups/web/w17-states.html` §5 and §6
// (T-211, on T-208's `liveState`).
//
// Everything else the shell says about an answer fits in the top bar (`Freshness`) or in §12.2's own band
// (`ConnectionBand`). These two do not, for the same reason: both are long sentences, and both have to be
// readable at 360 px — where the freshness caption is deliberately absent (DESIGN_SYSTEM §10's give-way
// order; `AppShell.module.css` hides it below 1400 after BUG-089's measurement). A state that only exists in
// the top bar is a state a phone-width reader never sees, so these live inside `<main>`.
//
//   §5 · **The first sync** (REQ-002 AC2, REQ-286 AC4). `state.first_sync` is 107 characters and T-210
//        measured it at 578 px in the top bar, where it starved the account name to "R…". Here it has room,
//        and it is joined by the two facts a caption could never carry: how far the pass has got, and **how
//        far back history has loaded** — the clause REQ-002 AC2 asks for by name.
//   §6 · **Past three hours** (REQ-283 AC2's second threshold). The amber chip says "may be out of date" in
//        the bar; past three hours the page says it, with the instant and a way to act.
//
// **Three rules this file follows and does not decide.**
//   1. It derives nothing. `live.state` is `liveState(...)` resolved once by `useShellLive`; `firstSync`
//      being non-null IS "this account has never been read" (API §2.3's own test — `dataAsOf === null` on
//      both the pass and the record), and `freshness.banner` being non-null IS "older than three hours". No
//      threshold, no instant arithmetic and no percentage is computed here (API §1.4, D-1078 (j)).
//   2. WHAT HAPPENED is the catalogue's sentence or the server's own; what it MEANS for the ads and what to
//      do next is the app's (D-540). That is why the bodies are `SHELL_COPY`'s and the titles are not.
//   3. **Nothing here can appear on sample data without an armed fault** (REQ-283 AC5, API §2.6). The demo
//      server's `dataAsOf` is the time of its own last evaluation, so `firstSync` is always null there; and
//      `liveFreshness` returns tone `sample` with `banner: null` for any demo account however old its
//      fixture. The `stale` fault is what reaches the amber chip (90 minutes back) — and nothing in a demo
//      reaches the three-hour banner, which is honest rather than a gap: the fault moves the instant 90
//      minutes, not three hours.

export interface LiveStatesProps {
  live: ShellLive;
}

export function LiveStates({ live }: LiveStatesProps) {
  const { messages } = useShell();
  const { state, refresh, requesting } = live;
  const firstSync = state.firstSync;

  return (
    <>
      {firstSync === null ? null : (
        <FirstSyncBanner
          sentence={messageText(firstSync.message, messages)}
          done={firstSync.done}
          total={firstSync.total}
          progressPct={firstSync.progressPct}
          historyLoadedBackTo={firstSync.historyLoadedBackTo}
        />
      )}
      {state.freshness.banner === null ? null : (
        /* w17 §6: "The banner appears … with level 2 only if the numbers are more than 3 hours old." Amber,
           because something is incomplete and we cannot fix it from here; `cloud-off`, because the reason is
           always that we could not reach Meta. The sentence is the server's own `stale_data` where it sent
           one and `warning.stale_data` composed from the same instant where it did not (D-1078 (i)) — never
           a third wording of the same fact. */
        <Banner
          tone="watch"
          icon="cloud-off"
          title={asSentence(messageText(state.freshness.banner, messages))}
          testID={testId('state', 'stale')}
          className={styles.band}
          actions={
            <Button
              variant="secondary"
              size="sm"
              icon="refresh"
              loading={requesting}
              testID={testId('state', 'stale', 'retry')}
              onClick={refresh}
            >
              {SHELL_COPY.retry}
            </Button>
          }
        >
          {SHELL_COPY.notAffectedHere}
        </Banner>
      )}
    </>
  );
}

/**
 * REQ-002 AC2's progress state, as w17 §5 draws it: one calm sentence, a track showing how far the pass has
 * got, and the date history has reached.
 *
 * **The track is a `role="progressbar"` and not a sentence**, which is what lets this state exist without a
 * new message key: a reader is told "3 of 5" / "67%" by the platform, in their own language, out of the two
 * numbers §2.3 already sends. Where the running step reports its own `progressPct` that is the honest figure
 * (on `history` it is the stored cursor's step over its plan's length — API §2.4 as built, which is exactly
 * w17's "60 of 90 days"); where it reports none the finished steps are the measure. A pass with no steps at
 * all is INDETERMINATE and says so by carrying no value, rather than claiming nought per cent.
 */
function FirstSyncBanner({
  sentence,
  done,
  total,
  progressPct,
  historyLoadedBackTo,
}: {
  sentence: string;
  done: number;
  total: number;
  progressPct: number | null;
  historyLoadedBackTo: IsoDate | null;
}) {
  const { messages } = useShell();
  const pct =
    progressPct !== null
      ? clampPct(progressPct)
      : total > 0
        ? clampPct((done / total) * 100)
        : null;
  return (
    <Banner
      tone="info"
      icon="loader"
      title={sentence}
      testID={testId('state', 'first-sync')}
      className={styles.band}
    >
      <span className={styles.firstSync}>
        <span
          className={styles.firstSyncTrack}
          role="progressbar"
          aria-label={sentence}
          aria-valuemin={pct === null ? undefined : 0}
          aria-valuemax={pct === null ? undefined : 100}
          aria-valuenow={pct ?? undefined}
          data-indeterminate={pct === null || undefined}
        >
          <span className={styles.firstSyncFill} style={{ width: `${pct ?? 0}%` }} />
        </span>
        {historyLoadedBackTo === null ? null : (
          /* REQ-002 AC2's "results back to 14 Aug…". `historyLoadedBackTo` is null until a pass has
             COMPLETED (API §2.4 as built: there is no honest date before that), so this clause arrives when
             there is something true to say and not a moment earlier. */
          <span
            className={styles.firstSyncNote}
            data-testid={testId('state', 'first-sync', 'history')}
          >
            {SHELL_COPY.firstSyncHistory(messages.format(historyLoadedBackTo, 'date.short'))}
          </span>
        )}
      </span>
    </Banner>
  );
}

/** A percentage off the wire is rendered, never computed (API §1.4) — but it is still clamped before it
 *  becomes a CSS width, because a width is geometry and 140 % of a track is a drawing bug, not a figure. */
function clampPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}
